import { motion } from "framer-motion";
import {
  ChefHat,
  CalendarCheck2,
  CheckCircle2,
  Clock4,
  TrendingUp,
  Bike,
  LineChart,
  ArrowUpRight,
  ArrowRight,
  Phone,
  MapPin,
  Globe,
  ShieldCheck,
  Zap,
  Users,
  Wallet,
  Sparkles,
  Coffee,
  Pizza,
  Beer,
  Utensils,
  Soup,
  Wine,
  Fish,
  Cake,
  Salad,
  Truck,
  PackageCheck,
} from "lucide-react";
import SlideShell from "@/components/SlideShell";
import Reveal from "@/components/Reveal";
import {
  WHATSAPP_URL,
  WHATSAPP_DISPLAY,
  WEBSITE_URL,
} from "@/data/constants";

const Eyebrow = ({ children, variant = "dark" }) => (
  <div
    className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${
      variant === "light" ? "text-orange-300" : "text-emerald-800"
    }`}
  >
    <span
      className={`block w-6 h-px ${variant === "light" ? "bg-orange-300" : "bg-emerald-800"}`}
    />
    {children}
  </div>
);

const FeatureItem = ({ icon: Icon, children, variant = "dark" }) => (
  <li
    className={`flex items-start gap-3 text-sm md:text-base ${
      variant === "light" ? "text-white/80" : "text-zinc-700"
    }`}
  >
    <span
      className={`mt-0.5 grid place-items-center w-6 h-6 rounded-full shrink-0 ${
        variant === "light" ? "bg-orange-400/15 text-orange-300" : "bg-orange-500/10 text-orange-600"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
    </span>
    <span>{children}</span>
  </li>
);

const SectionHeading = ({ eyebrow, title, accentWord, lead, variant = "dark" }) => (
  <>
    <Reveal><Eyebrow variant={variant}>{eyebrow}</Eyebrow></Reveal>
    <Reveal delay={0.1}>
      <h2
        className={`font-display font-black mt-5 text-3xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tighter ${
          variant === "light" ? "text-white" : "text-zinc-950"
        }`}
      >
        {title}{" "}
        {accentWord && (
          <span className={variant === "light" ? "text-orange-400" : "text-emerald-800"}>
            {accentWord}
          </span>
        )}
      </h2>
    </Reveal>
    {lead && (
      <Reveal delay={0.2}>
        <p
          className={`mt-5 text-base md:text-lg leading-relaxed max-w-xl ${
            variant === "light" ? "text-white/70" : "text-zinc-600"
          }`}
        >
          {lead}
        </p>
      </Reveal>
    )}
  </>
);

const TwoCol = ({ id, index, total, reverse, bg = "white", variant = "dark", children }) => (
  <SlideShell
    id={id}
    index={index}
    total={total}
    variant={variant}
    className={
      bg === "white"
        ? "bg-white"
        : bg === "muted"
        ? "bg-zinc-100"
        : bg === "emerald"
        ? "bg-emerald-950 text-white"
        : "bg-zinc-950 text-white"
    }
  >
    <div
      className={`relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      {children}
    </div>
  </SlideShell>
);

/* ============ SLIDE 11 — RECETAS Y COSTEOS ============ */

const RecipeMockup = () => (
  <div className="max-w-md mx-auto bg-white rounded-3xl ring-1 ring-zinc-200 shadow-xl overflow-hidden">
    <div className="px-5 py-4 bg-emerald-900 text-white">
      <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Receta · Plato fuerte</div>
      <div className="font-display font-black text-2xl mt-1">Rib eye 350g</div>
    </div>
    <div className="p-5 space-y-2.5">
      {[
        { name: "Rib eye 350g", cost: "$118.00" },
        { name: "Papas rústicas 180g", cost: "$14.50" },
        { name: "Mantequilla aromática", cost: "$6.80" },
        { name: "Guarnición vegetal", cost: "$9.40" },
      ].map((it, i) => (
        <motion.div
          key={it.name}
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: false }}
          transition={{ delay: 0.1 + i * 0.07 }}
          className="flex items-center justify-between text-sm"
        >
          <span className="text-zinc-700">{it.name}</span>
          <span className="font-display font-bold text-zinc-950">{it.cost}</span>
        </motion.div>
      ))}
    </div>
    <div className="px-5 py-4 bg-zinc-50 grid grid-cols-3 gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Costo</div>
        <div className="font-display font-black text-zinc-950 text-lg">$148.70</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Precio</div>
        <div className="font-display font-black text-zinc-950 text-lg">$420.00</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-orange-600">Margen</div>
        <div className="font-display font-black text-orange-600 text-lg">64.6%</div>
      </div>
    </div>
  </div>
);

const Recetas = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="white">
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="11 · Recetas y costeos"
        title="Costeo automático y"
        accentWord="margen real."
        lead="Calcula el costo real de cada platillo con base en ingredientes, porciones y precios de compra. Conoce cuánto ganas realmente por cada platillo."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={ChefHat}>Recetas con ingredientes y porciones.</FeatureItem>
          <FeatureItem icon={Wallet}>Costo actualizado automáticamente.</FeatureItem>
          <FeatureItem icon={TrendingUp}>Margen y utilidad por platillo.</FeatureItem>
          <FeatureItem icon={Sparkles}>Sugerencias de optimización.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><RecipeMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 12 — RESERVAS ============ */

const ReservationMockup = () => (
  <div className="max-w-md mx-auto bg-white rounded-3xl ring-1 ring-zinc-200 shadow-xl p-5">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Reservas · Viernes</div>
        <div className="font-display font-black text-zinc-950 text-xl">14 mesas confirmadas</div>
      </div>
      <CalendarCheck2 className="w-6 h-6 text-emerald-700" />
    </div>
    <div className="mt-5 space-y-2.5">
      {[
        { name: "Familia Hernández", people: 6, hour: "20:00", status: "confirmada" },
        { name: "Daniela Romero", people: 2, hour: "20:30", status: "confirmada" },
        { name: "Cumpleaños Luis", people: 12, hour: "21:00", status: "pendiente" },
      ].map((r, i) => (
        <motion.div
          key={r.name}
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ delay: 0.1 + i * 0.08 }}
          className="flex items-center justify-between bg-zinc-50 rounded-xl px-4 py-3"
        >
          <div>
            <div className="text-sm font-semibold text-zinc-950">{r.name}</div>
            <div className="text-[11px] text-zinc-500 flex items-center gap-2">
              <Users className="w-3 h-3" /> {r.people} personas · {r.hour}
            </div>
          </div>
          <span
            className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-full ${
              r.status === "confirmada"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-orange-100 text-orange-700"
            }`}
          >
            {r.status}
          </span>
        </motion.div>
      ))}
    </div>
  </div>
);

const Reservas = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="muted" reverse>
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="12 · Reservas"
        title="Sistema de reservas"
        accentWord="en línea."
        lead="Recibe, organiza y confirma reservas desde internet. Mejor control de mesas, menos llamadas, experiencia profesional."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={CalendarCheck2}>Mejor control de mesas por turno.</FeatureItem>
          <FeatureItem icon={Phone}>Menos llamadas y mensajes manuales.</FeatureItem>
          <FeatureItem icon={Sparkles}>Experiencia profesional para tu marca.</FeatureItem>
          <FeatureItem icon={CheckCircle2}>Confirmaciones automáticas por WhatsApp.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><ReservationMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 13 — REPORTES ============ */

const ReportsDashboard = () => (
  <div className="max-w-xl mx-auto bg-white rounded-3xl ring-1 ring-zinc-200 shadow-2xl p-6">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Hoy · 14:32</div>
        <div className="font-display font-black text-zinc-950 text-2xl">Reporte en vivo</div>
      </div>
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En vivo
      </span>
    </div>

    <div className="mt-5 grid grid-cols-3 gap-3">
      {[
        { label: "Ventas", value: "$24,820", delta: "+18%", color: "emerald" },
        { label: "Tickets", value: "186", delta: "+22%", color: "orange" },
        { label: "Promedio", value: "$133", delta: "+4%", color: "zinc" },
      ].map((m) => (
        <div key={m.label} className="rounded-xl bg-zinc-50 p-3 ring-1 ring-zinc-100">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{m.label}</div>
          <div className="font-display font-black text-zinc-950 text-lg mt-1">{m.value}</div>
          <div
            className={`mt-1 text-[10px] font-bold flex items-center gap-1 ${
              m.color === "emerald" ? "text-emerald-700" : m.color === "orange" ? "text-orange-600" : "text-zinc-600"
            }`}
          >
            <ArrowUpRight className="w-3 h-3" /> {m.delta}
          </div>
        </div>
      ))}
    </div>

    {/* Bar chart */}
    <div className="mt-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Ventas por hora</div>
      <div className="flex items-end gap-1.5 h-28">
        {[30, 45, 62, 80, 55, 48, 70, 88, 95, 72, 60, 78].map((h, i) => (
          <motion.div
            key={i}
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: false }}
            transition={{ delay: 0.1 + i * 0.04, duration: 0.5 }}
            className={`flex-1 rounded-t origin-bottom ${
              i === 8 ? "bg-orange-500" : "bg-emerald-800"
            }`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>

    <div className="mt-5 pt-4 border-t border-dashed border-zinc-200 grid grid-cols-2 gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Top platillo</div>
        <div className="font-display font-bold text-sm text-zinc-950 mt-1 flex items-center gap-1.5">
          <Utensils className="w-3.5 h-3.5 text-orange-500" /> Tacos al pastor · 42 órdenes
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Hora pico</div>
        <div className="font-display font-bold text-sm text-zinc-950 mt-1 flex items-center gap-1.5">
          <Clock4 className="w-3.5 h-3.5 text-orange-500" /> 14:00 - 15:00
        </div>
      </div>
    </div>
  </div>
);

const Reportes = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} variant="light" className="bg-emerald-950 text-white noise">
    <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-orange-500/15 blur-3xl" />
    <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-emerald-400/10 blur-3xl" />

    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-5">
        <SectionHeading
          variant="light"
          eyebrow="13 · Reportes"
          title="Decisiones con"
          accentWord="datos reales."
          lead="Reportes de ventas, compras y utilidades en tiempo real. Consulta información clave desde cualquier lugar."
        />
        <Reveal delay={0.3}>
          <ul className="mt-8 space-y-3.5">
            <FeatureItem icon={LineChart} variant="light">Ventas por día, semana, mes y año.</FeatureItem>
            <FeatureItem icon={TrendingUp} variant="light">Productos más vendidos y rentables.</FeatureItem>
            <FeatureItem icon={Clock4} variant="light">Horarios fuertes y ticket promedio.</FeatureItem>
            <FeatureItem icon={Wallet} variant="light">Compras, gastos y utilidades.</FeatureItem>
          </ul>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.2}><ReportsDashboard /></Reveal>
      </div>
    </div>
  </SlideShell>
);

/* ============ SLIDE 14 — DELIVERY PROPIO ============ */

const DeliveryMockup = () => (
  <div className="max-w-md mx-auto bg-white rounded-3xl ring-1 ring-zinc-200 shadow-xl overflow-hidden">
    <div className="relative h-44 bg-[radial-gradient(circle_at_30%_30%,#F97316_0%,#064E3B_60%)]">
      <div className="absolute inset-0 grid place-items-center">
        <motion.div
          animate={{ x: [-10, 10, -10] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="grid place-items-center w-20 h-20 rounded-full bg-white/10 ring-1 ring-white/20"
        >
          <Bike className="w-10 h-10 text-white" />
        </motion.div>
      </div>
      <div className="absolute bottom-3 left-4 text-white text-[10px] uppercase tracking-[0.22em] opacity-80">
        Repartidor · José M.
      </div>
    </div>
    <div className="p-5 space-y-3">
      {[
        { label: "Asignado", icon: PackageCheck, time: "12:30" },
        { label: "En camino", icon: Truck, time: "12:42" },
        { label: "Entregado", icon: CheckCircle2, time: "—" },
      ].map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: false }}
          transition={{ delay: 0.1 + i * 0.1 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <span
              className={`grid place-items-center w-8 h-8 rounded-lg ${
                i < 2 ? "bg-emerald-50 text-emerald-800" : "bg-zinc-100 text-zinc-500"
              }`}
            >
              <s.icon className="w-4 h-4" />
            </span>
            <div className="text-sm font-semibold text-zinc-950">{s.label}</div>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono">{s.time}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

const Delivery = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="white" reverse>
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="14 · Delivery propio"
        title="Delivery propio sin pagar"
        accentWord="comisiones."
        lead="Administra tus pedidos, repartidores y entregas desde tu propio sistema. Tus clientes, tu margen, tu marca."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={ShieldCheck}>Sin comisiones por venta a terceros.</FeatureItem>
          <FeatureItem icon={Truck}>Control total de repartidores activos.</FeatureItem>
          <FeatureItem icon={Users}>Base de clientes propios y recurrentes.</FeatureItem>
          <FeatureItem icon={TrendingUp}>Mayor rentabilidad por pedido.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><DeliveryMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 15 — CLIENTES / RESTAURANTES ============ */

const CLIENT_TYPES = [
  { icon: Utensils, label: "Restaurantes" },
  { icon: Coffee, label: "Cafeterías" },
  { icon: Beer, label: "Bares" },
  { icon: Pizza, label: "Pizzerías" },
  { icon: Fish, label: "Marisquerías" },
  { icon: Soup, label: "Taquerías" },
  { icon: Cake, label: "Reposterías" },
  { icon: Salad, label: "Healthy" },
  { icon: Wine, label: "Vinotecas" },
  { icon: ChefHat, label: "Dark kitchens" },
];

const Clientes = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} className="bg-zinc-100">
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>15 · Clientes</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black mt-5 text-3xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tighter text-zinc-950">
              Negocios gastronómicos que ya{" "}
              <span className="text-emerald-800">confían</span> en ERP Alimentos.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <p className="text-zinc-600 text-base md:text-lg leading-relaxed">
              ERP Alimentos se adapta a diferentes tipos de negocios
              gastronómicos. Cada operación tiene su propia receta.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
        {CLIENT_TYPES.map((c, i) => (
          <Reveal key={c.label} delay={0.04 * i}>
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-5 md:p-6 ring-1 ring-zinc-200 hover:ring-emerald-700/40 hover:shadow-lg transition-all flex flex-col gap-3"
            >
              <c.icon className="w-6 h-6 text-orange-500" />
              <div className="font-display font-bold text-zinc-950 text-base">{c.label}</div>
            </motion.div>
          </Reveal>
        ))}
      </div>

      {/* Marquee testimonial */}
      <div className="mt-12 overflow-hidden border-y border-zinc-200 py-4">
        <div className="marquee-track flex gap-12 text-zinc-400 font-display font-bold text-xl uppercase tracking-tight">
          {[
            "La Casa del Chef",
            "Pizza Romana",
            "Mariscos del Puerto",
            "Café Aurora",
            "Taquería El Buen Pastor",
            "Sazón & Brasa",
            "Dark Kitchen Lab",
            "Cantina Maguey",
            "La Casa del Chef",
            "Pizza Romana",
            "Mariscos del Puerto",
            "Café Aurora",
            "Taquería El Buen Pastor",
            "Sazón & Brasa",
            "Dark Kitchen Lab",
            "Cantina Maguey",
          ].map((name, i) => (
            <span key={i} className="whitespace-nowrap inline-flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
              {name}
            </span>
          ))}
        </div>
      </div>
    </div>
  </SlideShell>
);

/* ============ SLIDE 16 — CONTRATACIÓN ============ */

const STEPS = [
  { n: "01", title: "Solicita información", desc: "Habla con un asesor por WhatsApp o sitio web." },
  { n: "02", title: "Creamos tu cuenta", desc: "Configuración en la nube en pocas horas." },
  { n: "03", title: "Configuramos tu restaurante", desc: "Áreas, mesas, sucursales, impresoras." },
  { n: "04", title: "Cargas menú y productos", desc: "Te acompañamos en la implementación." },
  { n: "05", title: "Vendes y administras", desc: "Operación digitalizada desde el día uno." },
];

const Contratacion = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} className="bg-white">
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>16 · Contratación</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black mt-5 text-3xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tighter text-zinc-950">
              Contrata ERP Alimentos y{" "}
              <span className="text-orange-500">digitaliza</span> tu restaurante.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <ul className="space-y-2 text-sm text-zinc-700">
              {[
                "Sistema 100% en la nube",
                "Acceso desde cualquier dispositivo",
                "Sin instalaciones complicadas",
                "Ideal para negocios pequeños, medianos y grandes",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>

      <ol className="relative grid md:grid-cols-5 gap-4 md:gap-6">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.05 * i}>
            <li className="relative h-full bg-zinc-50 rounded-2xl p-5 md:p-6 ring-1 ring-zinc-100 hover:ring-emerald-700/30 transition">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-600 font-bold">
                  Paso {s.n}
                </div>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-zinc-300 hidden md:block" />
                )}
              </div>
              <div className="font-display font-bold text-zinc-950 text-base md:text-lg mt-4">
                {s.title}
              </div>
              <div className="mt-2 text-xs md:text-sm text-zinc-600 leading-relaxed">
                {s.desc}
              </div>
            </li>
          </Reveal>
        ))}
      </ol>

      <Reveal delay={0.5}>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <motion.a
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.96 }}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-contratacion-info"
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-6 py-3.5 transition-colors shadow-[0_8px_30px_-8px_rgba(249,115,22,0.6)]"
          >
            Solicitar información <ArrowRight className="w-4 h-4" />
          </motion.a>
          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-2"
            data-testid="cta-contratacion-web"
          >
            <Globe className="w-4 h-4" /> {WEBSITE_URL.replace("https://", "")}
          </a>
        </div>
      </Reveal>
    </div>
  </SlideShell>
);

/* ============ SLIDE 17 — CONTACTO ============ */

const WhatsAppGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Contacto = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} variant="light" className="bg-zinc-950 text-white noise">
    <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-emerald-700/25 blur-3xl" />
    <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-orange-500/15 blur-3xl" />

    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20">
      <Reveal><Eyebrow variant="light">17 · Contacto</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display font-black mt-5 text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tighter max-w-4xl">
          Empieza hoy con{" "}
          <span className="text-orange-400">ERP Alimentos.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-6 text-white/70 text-base md:text-xl leading-relaxed max-w-2xl">
          Tu restaurante merece una herramienta que trabaje tan duro como tú.
        </p>
      </Reveal>

      <div className="mt-12 grid lg:grid-cols-12 gap-6 items-stretch">
        {/* Contact cards */}
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-card-whatsapp"
            className="group glass rounded-2xl p-6 hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#25D366] text-white">
                <WhatsAppGlyph className="w-5 h-5" />
              </div>
              <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-orange-400 transition" />
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-[0.22em] text-white/50">
              WhatsApp
            </div>
            <div className="font-display font-bold text-xl mt-1">{WHATSAPP_DISPLAY}</div>
          </a>

          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-card-web"
            className="group glass rounded-2xl p-6 hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="grid place-items-center w-11 h-11 rounded-xl bg-orange-500 text-white">
                <Globe className="w-5 h-5" />
              </div>
              <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-orange-400 transition" />
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-[0.22em] text-white/50">
              Sitio web
            </div>
            <div className="font-display font-bold text-xl mt-1">
              {WEBSITE_URL.replace("https://", "")}
            </div>
          </a>

          <div className="sm:col-span-2 glass rounded-2xl p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Cobertura</div>
            <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-400" />
              México · Servicio 100% en la nube, desde cualquier dispositivo.
            </div>
          </div>
        </div>

        {/* Big CTAs */}
        <div className="lg:col-span-5 flex flex-col gap-3">
          <motion.a
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-contacto-whatsapp"
            className="group rounded-2xl bg-[#25D366] hover:bg-[#1ebd5b] text-white p-6 flex items-center justify-between transition-colors shadow-xl"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Acción rápida</div>
              <div className="font-display font-black text-2xl mt-1">Contactar por WhatsApp</div>
            </div>
            <WhatsAppGlyph className="w-10 h-10 opacity-90 group-hover:scale-110 transition-transform" />
          </motion.a>

          <motion.a
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-contacto-cuenta"
            className="group rounded-2xl bg-orange-500 hover:bg-orange-600 text-white p-6 flex items-center justify-between transition-colors shadow-xl"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Empezar</div>
              <div className="font-display font-black text-2xl mt-1">Crear cuenta</div>
            </div>
            <ArrowRight className="w-8 h-8 opacity-90 group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="text-xs text-white/40">
          © {new Date().getFullYear()} ERP Alimentos · Sistema integral para restaurantes y negocios gastronómicos.
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          Presentación comercial · v1.0
        </div>
      </div>
    </div>
  </SlideShell>
);

export const SLIDES_PART_C = {
  recetas: Recetas,
  reservas: Reservas,
  reportes: Reportes,
  delivery: Delivery,
  clientes: Clientes,
  contratacion: Contratacion,
  contacto: Contacto,
};
